import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { DealAttributeDefinitionApiService } from '../services/deal-attribute-definition-api.service';
import {
  DealAttributeDefinitionRequest,
  DealAttributeType,
} from '../models/deal-attribute-definition.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { PreventSpecialCharactersDirective } from '../../../shared/directives/prevent-special-characters.directive';
import { PreventLongNamesDirective } from '../../../shared/directives/prevent-long-names.directive';
import { ValidatorFormsInformation } from '../../../shared/utils/validator-forms-information.utils';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-deal-attribute-definition-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    PreventSpecialCharactersDirective,
    PreventLongNamesDirective,
  ],
  templateUrl: './deal-attribute-definition-form.component.html',
})
export class DealAttributeDefinitionFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  // Form fields
  name = '';
  private originalName = '';
  type: DealAttributeType = 'text';
  listOptionsText = '';
  isActive = true;

  readonly typeOptions: { value: DealAttributeType; label: string }[] = [
    { value: 'text', label: 'Texto' },
    { value: 'number', label: 'Número' },
    { value: 'boolean', label: 'Booleano' },
    { value: 'list', label: 'Lista' },
  ];

  constructor(
    private api: DealAttributeDefinitionApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.itemId = +id;
      this.cargar(this.itemId);
    }
  }

  setError(message: string): void {
    this.error = message;
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';

    this.api.getById(id).subscribe({
      next: (item) => {
        this.name = item.name;
        this.originalName = item.name;
        this.type = item.type;
        this.isActive = item.isActive;
        if (item.listOptions) {
          try {
            const parsed = JSON.parse(item.listOptions);
            this.listOptionsText = Array.isArray(parsed)
              ? parsed.join('\n')
              : item.listOptions;
          } catch {
            this.listOptionsText = item.listOptions;
          }
        }
        this.loading = false;
      },
      error: (err) => {
        this.error =
          err.error?.message || 'Error al cargar el campo personalizado';
        this.loading = false;
      },
    });
  }

  get pageTitle(): string {
    return this.isEditing
      ? 'Editar Campo Personalizado'
      : 'Nuevo Campo Personalizado';
  }

  guardar(): void {
    const nameValidation = ValidatorFormsInformation.validateText(
      this.name,
      'nombre',
      30,
    );

    if (nameValidation) {
      this.error = nameValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) {
      this.error = 'No se pudo obtener el negocio del usuario';
      this.toast.error({ title: 'Error', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';

    let listOptions: string | null = null;
    if (this.type === 'list' && this.listOptionsText.trim()) {
      const options = this.listOptionsText.split('\n').filter((o) => o.trim());
      listOptions = JSON.stringify(options);
    }

    const request: DealAttributeDefinitionRequest = {
      name: this.name.trim(),
      type: this.type,
      listOptions,
      isActive: this.isActive,
      businessId,
    };

    if (this.isEditing && this.itemId) {
      this.api.update(this.itemId, request).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config-deals/definiciones']);
          this.toast.updated('Campo personalizado',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
        },
        error: (err) => {
          this.saving = false;
          this.error =
            err.error?.message || 'Error al actualizar el campo personalizado';
          if (
            err.error?.message ===
            `Deal attribute with name '${request.name}' already exists for this business`
          ) {
            this.error = `Ya existe un campo personalizado con el nombre '${request.name}' en esta empresa`;
            this.toast.error({
              title: 'Error',
              message: this.error,
            });
          } else {
            this.toast.error({ title: 'Error', message: this.error });
          }
        },
      });
    } else {
      this.api.create(request).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config-deals/definiciones']);
          this.toast.created('Campo personalizado', `Se creó "${this.name.trim()}" correctamente.`);
        },
        error: (err) => {
          this.saving = false;
          this.error =
            err.error?.message || 'Error al actualizar el campo personalizado';
          if (
            err.error?.message ===
            `Deal attribute with name '${request.name}' already exists for this business`
          ) {
            this.error = `Ya existe un campo personalizado con el nombre '${request.name}' en esta empresa`;
            this.toast.error({
              title: 'Error',
              message: this.error,
            });
          } else {
            this.toast.error({ title: 'Error', message: this.error });
          }
        },
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-deals/definiciones']);
  }
}
